import {
    ProductionOverviewCellInput,
    ProductionOverviewCount,
    ProductionOverviewCountMode,
    ProductionOverviewDayCount,
    ProductionOverviewPeriod,
    ProductionOverviewRowKey,
} from 'models/production-overview';

/** The custom range may span at most one month (DD-3800). */
export const MAX_CUSTOM_RANGE_DAYS = 31;

export const EMPTY_COUNT: ProductionOverviewCount = { orderCount: 0, actionCount: 0 };

/** "YYYY-MM-DD" in the browser's local calendar — the same day the user picked. */
export const formatDayKey = (date: Date): string => {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const dayOfMonth = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${dayOfMonth}`;
};

export const addDays = (date: Date, dayCount: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + dayCount);
    return result;
};

export const getTodayKey = (): string => formatDayKey(new Date());

/** Every day key from `fromDate` to `toDate`, both inclusive. */
export const buildDayKeys = (fromDate: Date, toDate: Date): string[] => {
    const dayKeys: string[] = [];
    for (let cursor = new Date(fromDate); formatDayKey(cursor) <= formatDayKey(toDate); cursor = addDays(cursor, 1)) {
        dayKeys.push(formatDayKey(cursor));
    }
    return dayKeys;
};

const parseDayKey = (dayKey: string): Date => {
    const [year, month, dayOfMonth] = dayKey.split('-').map(Number);
    return new Date(year, month - 1, dayOfMonth);
};

export const isWeekendDay = (dayKey: string): boolean => {
    const weekday = parseDayKey(dayKey).getDay();
    return weekday === 0 || weekday === 6;
};

/** Mondays carry the thin line that separates the weeks. */
export const isMonday = (dayKey: string): boolean => parseDayKey(dayKey).getDay() === 1;

export const getWeekdayLabel = (dayKey: string): string =>
    new Intl.DateTimeFormat(undefined, { weekday: 'short' }).format(parseDayKey(dayKey));

export const getDayOfMonthLabel = (dayKey: string): string => {
    const date = parseDayKey(dayKey);
    return `${date.getDate()}/${date.getMonth() + 1}`;
};

export type CustomRangeValidationError = 'RANGE_END_BEFORE_START' | 'RANGE_TOO_LONG' | null;

export const validateCustomRange = (fromDate: Date | null, toDate: Date | null): CustomRangeValidationError => {
    if (!fromDate || !toDate) return null;
    const from = parseDayKey(formatDayKey(fromDate));
    const to = parseDayKey(formatDayKey(toDate));
    if (to < from) return 'RANGE_END_BEFORE_START';
    const dayCount = Math.round((to.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)) + 1;
    if (dayCount > MAX_CUSTOM_RANGE_DAYS) return 'RANGE_TOO_LONG';
    return null;
};

export const resolveDateRange = (
    period: ProductionOverviewPeriod,
    startDate: Date,
    customFromDate: Date | null,
    customToDate: Date | null,
): { fromDate: Date; toDate: Date } | null => {
    if (period === ProductionOverviewPeriod.FOURTEEN_DAYS)
        return { fromDate: startDate, toDate: addDays(startDate, 13) };
    if (period === ProductionOverviewPeriod.THIRTY_DAYS) return { fromDate: startDate, toDate: addDays(startDate, 29) };
    if (!customFromDate || !customToDate || validateCustomRange(customFromDate, customToDate)) return null;
    return { fromDate: customFromDate, toDate: customToDate };
};

export const pickCount = (count: ProductionOverviewCount | undefined, mode: ProductionOverviewCountMode): number => {
    if (!count) return 0;
    return mode === ProductionOverviewCountMode.ACTIONS ? count.actionCount : count.orderCount;
};

export const dayCountsToMap = (
    dayCounts: ProductionOverviewDayCount[] | undefined,
): Map<string, ProductionOverviewCount> => new Map((dayCounts || []).map((dayCount) => [dayCount.day, dayCount]));

export const addCounts = (
    first: ProductionOverviewCount,
    second: ProductionOverviewCount,
): ProductionOverviewCount => ({
    orderCount: first.orderCount + second.orderCount,
    actionCount: first.actionCount + second.actionCount,
});

/**
 * Heat shading relative to the highest visible count, on the Production planning
 * grey scale (production-dashboard/utils.ts).
 */
export const getHeatColor = (count: number, maxCount: number): string => {
    if (count <= 0 || maxCount <= 0) return 'transparent';
    const ratio = count / maxCount;
    if (ratio <= 0.17) return '#f5f5f5';
    if (ratio <= 0.33) return '#eeeeee';
    if (ratio <= 0.5) return '#e0e0e0';
    if (ratio <= 0.67) return '#d6d6d6';
    if (ratio <= 0.83) return '#c8c8c8';
    return '#b8b8b8';
};

// --- Calendar row/group models the page builds from the query data ---

export type OverviewCalendarRow = {
    rowKey: ProductionOverviewRowKey;
    labelKey: string;
    countsByDay: Map<string, ProductionOverviewCount>;
    /** A right-now count drawn in today's column only (Inbox, Without deadline). */
    pointInTimeOnly?: boolean;
    /** Deadline row only: today's cell also shows how many orders are late. */
    lateToday?: ProductionOverviewCount;
};

export type OverviewCalendarGroup = {
    groupKey: string;
    labelKey: string;
    rows: OverviewCalendarRow[];
    /** Inbox and Received show a per-day total on the group header; Production does not. */
    showDayTotals: boolean;
    /** Inbox only: a right-now count drawn on the header itself, in today's column. */
    pointInTimeHeaderCount?: ProductionOverviewCount;
    /** Inbox only: the cell behind the header count (the group has no sub-rows). */
    headerRowKey?: ProductionOverviewRowKey;
    /** Received only: one aggregate cell (RECEIVED_ALL + day) behind each header total. */
    groupTotalRowKey?: ProductionOverviewRowKey;
};

export const sumGroupCountsForDay = (group: OverviewCalendarGroup, dayKey: string): ProductionOverviewCount =>
    group.rows.reduce((total, row) => addCounts(total, row.countsByDay.get(dayKey) || EMPTY_COUNT), EMPTY_COUNT);

/** The cell input whose click-through list holds exactly the orders behind one row/day cell. */
export const buildRowCellInput = (row: OverviewCalendarRow, dayKey: string): ProductionOverviewCellInput => {
    if (row.pointInTimeOnly) return { rowKey: row.rowKey };
    return { rowKey: row.rowKey, day: dayKey };
};

/** The highest count shown anywhere in the calendar, used as the heat-shading reference. */
export const getVisibleMaxCount = (
    groups: OverviewCalendarGroup[],
    days: string[],
    mode: ProductionOverviewCountMode,
): number => {
    let maxCount = 0;
    for (const group of groups) {
        if (group.pointInTimeHeaderCount) {
            maxCount = Math.max(maxCount, pickCount(group.pointInTimeHeaderCount, mode));
        }
        for (const day of days) {
            if (group.showDayTotals) maxCount = Math.max(maxCount, pickCount(sumGroupCountsForDay(group, day), mode));
            for (const row of group.rows) {
                maxCount = Math.max(maxCount, pickCount(row.countsByDay.get(day), mode));
            }
        }
    }
    return maxCount;
};
